import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import Swal from 'sweetalert2';
import { getCheckins, getEventNotes, saveEventNotes, saveTamuNote, updateTamuMeta, PRINT_URL } from '../../Api';
import iconGift from '../../assets/images/icon-gift.svg';
import iconTamu from '../../assets/images/icon-tamu.svg';
import iconClock from '../../assets/images/icon-clock.svg';
import iconUsher from '../../assets/images/icon-usher.svg';
import iconPrint from '../../assets/images/icon-print.svg';
import iconNote from '../../assets/images/icon-note.svg';
import './Tamu.css';

/** "2026-09-26 19:00:00" -> "26 SEP 2026, 19.00" */
function formatCheckinTime(raw) {
  if (!raw) return null;
  const d = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return raw;

  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MEI', 'JUN', 'JUL', 'AGU', 'SEP', 'OKT', 'NOV', 'DES'];
  const pad = (n) => String(n).padStart(2, '0');

  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}.${pad(d.getMinutes())}`;
}

/** Bluetooth Print hand-off, same scheme the scanner uses. */
function printUrl(guest, usherName, tempCat) {
  const q = (v) => encodeURIComponent(v ?? '');
  return (
    `my.bluetoothprint.scheme://${PRINT_URL}` +
    `?title=${q(guest.title)}&table=${q(guest.table)}&total=${q(guest.pax || guest.pax_allowed)}` +
    `&group=${q(guest.group)}&usher=${q(usherName)}&temp_cat=${q(tempCat)}`
  );
}

function GuestCard({ guest, canEdit, onEdited }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hadir, setHadir] = useState(guest.hadir);
  const [gift, setGift] = useState(guest.bawa_amplop === 'bawa');
  const [saving, setSaving] = useState(false);
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState(guest.catatan || '');

  const time = formatCheckinTime(guest.checkin_time);
  const broughtGift = guest.bawa_amplop === 'bawa';
  const dewasa = parseInt(guest.pax, 10) || 0;
  const anak = parseInt(guest.kids, 10) || 0;
  const usherName = localStorage.getItem('name') || '';
  const tempCat = localStorage.getItem('temp_cat') || '';

  const openEditor = () => {
    setHadir(guest.hadir);
    setGift(guest.bawa_amplop === 'bawa');
    setMenuOpen(false);
    setEditing(true);
  };

  const openNote = () => {
    setNote(guest.catatan || '');
    setMenuOpen(false);
    setNoting(true);
  };

  const saveNote = async () => {
    try {
      setSaving(true);
      await saveTamuNote(guest.id, note);
      setNoting(false);
      onEdited();
    } catch (err) {
      console.error('Save tamu note error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal menyimpan catatan.' });
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      await updateTamuMeta(guest.id, {
        hadir: hadir ? '1' : '0',
        kedatangan: String(guest.pax || guest.pax_allowed),
        bawa_amplop: gift ? 'bawa' : 'tidak',
      });
      setEditing(false);
      onEdited();
    } catch (err) {
      console.error('Update check-in error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal menyimpan perubahan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="guest-card">
      <header className="guest-card__head">
        <div className="guest-card__ident">
          <h4 className="guest-card__name">{guest.title}</h4>
          {guest.jabatan && <p className="guest-card__jabatan">{guest.jabatan}</p>}
          {guest.kategori && <p className="guest-card__kategori">{guest.kategori}</p>}
        </div>

        {canEdit && (
          <div className="guest-card__menu">
            <button
              type="button"
              className="guest-card__menu-btn"
              aria-label="Opsi tamu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span />
              <span />
              <span />
            </button>

            {menuOpen && (
              <>
                <div className="guest-card__menu-scrim" onClick={() => setMenuOpen(false)} />
                <div className="guest-card__dropdown">
                  <a
                    className="guest-card__dropdown-item"
                    href={printUrl(guest, usherName, tempCat)}
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                  >
                    <img src={iconPrint} alt="" /> Print Label
                  </a>
                  <button type="button" className="guest-card__dropdown-item" onClick={openEditor}>
                    <img src={iconGift} alt="" /> Edit Check-in
                  </button>
                  <button type="button" className="guest-card__dropdown-item" onClick={openNote}>
                    <img src={iconNote} alt="" /> Catatan
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      {guest.table && (
        <p className="guest-card__row">
          <img className="guest-card__row-icon" src={iconTamu} alt="" />
          <strong>{guest.table}</strong>
        </p>
      )}

      <p className="guest-card__row">
        <img className="guest-card__row-icon" src={iconClock} alt="" />
        {time || <em className="guest-card__muted">Waktu tidak tercatat</em>}
      </p>

      <p className="guest-card__row" title="Usher yang melakukan check-in">
        <img className="guest-card__row-icon" src={iconUsher} alt="" />
        {guest.checkin_usher ? (
          <strong>{guest.checkin_usher}</strong>
        ) : (
          <em className="guest-card__muted">Usher tidak tercatat</em>
        )}
      </p>

      {guest.catatan && (
        <p className="guest-card__row guest-card__note" title="Catatan usher">
          <img className="guest-card__row-icon" src={iconNote} alt="" />
          <span>{guest.catatan}</span>
        </p>
      )}

      <footer className="guest-card__foot">
        <span className="guest-card__pax">
          <img className="guest-card__row-icon" src={iconTamu} alt="" />
          <span className="guest-card__pax-text">
            {/* Same rule as the dashboard: hadir = dewasa (pax) + anak (kids). */}
            {dewasa + anak} / {guest.pax_allowed} Pax
            <small className="guest-card__pax-split">
              {dewasa} Dewasa {'·'} {anak} Anak
            </small>
          </span>
        </span>

        {broughtGift && (
          <span className="guest-card__tag">
            <img className="guest-card__tag-icon" src={iconGift} alt="" /> Amplop
          </span>
        )}
      </footer>

      {editing && (
        <div className="guest-modal" role="dialog" aria-modal="true">
          <div className="guest-modal__scrim" onClick={() => !saving && setEditing(false)} />
          <div className="guest-modal__box">
            <h4 className="guest-modal__name">{guest.title}</h4>
            {guest.jabatan && <p className="guest-modal__jabatan">{guest.jabatan}</p>}
            <p className="guest-modal__meta">
              {[guest.kategori, guest.table].filter(Boolean).join(' \u00b7 ')}
            </p>

            <label className="guest-modal__toggle">
              <input
                type="checkbox"
                checked={hadir}
                disabled={saving}
                onChange={(e) => setHadir(e.target.checked)}
              />
              <span>Sudah Check-in</span>
            </label>

            <label className="guest-modal__toggle">
              <input
                type="checkbox"
                checked={gift}
                disabled={saving}
                onChange={(e) => setGift(e.target.checked)}
              />
              <span>Bawa Amplop / Gift</span>
            </label>

            <p className="guest-modal__hint">
              Menonaktifkan check-in akan menghapus waktu dan nama usher yang tercatat.
            </p>

            <div className="guest-modal__actions">
              <button
                type="button"
                className="guest-modal__cancel"
                disabled={saving}
                onClick={() => setEditing(false)}
              >
                Batal
              </button>
              <button
                type="button"
                className="guest-modal__save"
                disabled={saving}
                onClick={save}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {noting && (
        <div className="guest-modal" role="dialog" aria-modal="true">
          <div className="guest-modal__scrim" onClick={() => !saving && setNoting(false)} />
          <div className="guest-modal__box">
            <h4 className="guest-modal__name">{guest.title}</h4>
            {guest.jabatan && <p className="guest-modal__jabatan">{guest.jabatan}</p>}
            <p className="guest-modal__meta">
              {[
                guest.kategori && `Kategori: ${guest.kategori}`,
                guest.table && `Grup / Table: ${guest.table}`,
              ]
                .filter(Boolean)
                .join(' \u00b7 ')}
            </p>

            <label className="tamu-notes__label" htmlFor={`catatan-${guest.id}`}>Catatan</label>
            <textarea
              id={`catatan-${guest.id}`}
              className="tamu-notes__text"
              rows={6}
              placeholder="Tulis catatan untuk tamu ini . . ."
              value={note}
              disabled={saving}
              onChange={(e) => setNote(e.target.value)}
            />

            <div className="guest-modal__actions">
              <button
                type="button"
                className="guest-modal__cancel"
                disabled={saving}
                onClick={() => setNoting(false)}
              >
                Batal
              </button>
              <button
                type="button"
                className="guest-modal__save"
                disabled={saving || note === (guest.catatan || '')}
                onClick={saveNote}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

/** Usher-only notes for the event, shared by every usher on it. */
function EventNotes({ customer }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const busy = loading || saving;

  const openNotes = async () => {
    setOpen(true);
    try {
      setLoading(true);
      const { notes: stored } = await getEventNotes(customer);
      setNotes(stored || '');
      setDraft(stored || '');
    } catch (err) {
      console.error('Load notes error:', err);
      setDraft(notes);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      const { notes: stored } = await saveEventNotes(customer, draft);
      setNotes(stored || '');
      setOpen(false);
    } catch (err) {
      console.error('Save notes error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal menyimpan catatan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button type="button" className="tamu-notes-btn" onClick={openNotes}>
        Notes
      </button>

      {open && (
        <div className="guest-modal" role="dialog" aria-modal="true">
          <div className="guest-modal__scrim" onClick={() => !saving && setOpen(false)} />
          <div className="guest-modal__box guest-modal__box--wide">
            <h4 className="guest-modal__name">Notes</h4>
            <p className="guest-modal__meta">Catatan untuk semua usher di event ini.</p>

            <textarea
              className="tamu-notes__text"
              rows={10}
              placeholder={loading ? 'Memuat catatan . . .' : 'Tulis catatan untuk event ini . . .'}
              value={draft}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
            />

            <div className="guest-modal__actions">
              <button
                type="button"
                className="guest-modal__cancel"
                disabled={saving}
                onClick={() => setOpen(false)}
              >
                Batal
              </button>
              <button
                type="button"
                className="guest-modal__save"
                disabled={busy || draft === notes}
                onClick={save}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Tamu() {
  const location = useLocation();

  const [data, setData] = useState({ total: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [topSearch, setTopSearch] = useState('');
  const [query, setQuery] = useState(location.state?.q || '');
  const [kategori, setKategori] = useState('');
  const [table, setTable] = useState('');
  const [usher, setUsher] = useState('');

  const customer = parseInt(localStorage.getItem('selectedCustomer'), 10);
  const isUsher = (localStorage.getItem('role') || 'usher') === 'usher';

  const load = useCallback(
    async (q) => {
      if (!customer) {
        setError('Client belum dipilih. Silakan login ulang.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setData(await getCheckins(customer, q ? { q } : {}));
        setError('');
      } catch (err) {
        console.error('Check-in history error:', err);
        setError('Gagal memuat riwayat check-in.');
      } finally {
        setLoading(false);
      }
    },
    [customer]
  );

  useEffect(() => {
    // Arriving from the Dashboard search carries the term in navigation state;
    // keep the visible filter in step with it.
    const incoming = location.state?.q || '';
    setQuery(incoming);
    load(incoming);
  }, [load, location.state]);

  // Filter options come from whatever the data actually contains.
  const kategoriOptions = useMemo(
    () => [...new Set(data.results.map((g) => g.kategori).filter(Boolean))].sort(),
    [data.results]
  );

  const tableOptions = useMemo(
    () => [...new Set(data.results.map((g) => g.table).filter(Boolean))].sort(),
    [data.results]
  );

  const usherOptions = useMemo(
    () => [...new Set(data.results.map((g) => g.checkin_usher).filter(Boolean))].sort(),
    [data.results]
  );

  const visible = useMemo(
    () =>
      data.results.filter(
        (g) =>
          (!kategori || g.kategori === kategori) &&
          (!table || g.table === table) &&
          (!usher || g.checkin_usher === usher) &&
          (!query || g.title.toLowerCase().includes(query.toLowerCase()))
      ),
    [data.results, kategori, table, usher, query]
  );

  return (
    <AppLayout
      searchValue={topSearch}
      onSearchChange={setTopSearch}
      onSearchSubmit={() => {
        setQuery(topSearch.trim());
        load(topSearch.trim());
      }}
    >
      <header className="tamu-head">
        <div>
          <h2>Riwayat Check-in</h2>
          <p>Menampilkan riwayat tamu yang sudah chek-in</p>
        </div>
        {isUsher && customer > 0 && <EventNotes customer={customer} />}
      </header>

      <div className="tamu-filters">
        <label className="tamu-filter">
          <span>Cari Riwayat Check-in</span>
          <input
            type="text"
            placeholder="Masukkan nama tamu . . ."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        <label className="tamu-filter">
          <span>Kategori</span>
          <select value={kategori} onChange={(e) => setKategori(e.target.value)}>
            <option value="">
              {kategoriOptions.length ? 'Pilih Kategori...' : 'Kategori belum tersedia'}
            </option>
            {kategoriOptions.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </label>

        <label className="tamu-filter">
          <span>Grup / Table</span>
          <select value={table} onChange={(e) => setTable(e.target.value)}>
            <option value="">Pilih Grup / Table...</option>
            {tableOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>

        <label className="tamu-filter">
          <span>Usher</span>
          <select value={usher} onChange={(e) => setUsher(e.target.value)}>
            <option value="">
              {usherOptions.length ? 'Pilih Usher...' : 'Usher belum tercatat'}
            </option>
            {usherOptions.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </label>
      </div>

      {loading && <p className="tamu-msg">Memuat riwayat . . .</p>}

      {!loading && error && (
        <div className="tamu-msg tamu-msg--error">
          <p>{error}</p>
          <button type="button" onClick={() => load(query)}>Coba lagi</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="tamu-count">
            {visible.length === data.results.length
              ? `${data.total.toLocaleString('id-ID')} tamu sudah check-in`
              : `Menampilkan ${visible.length.toLocaleString('id-ID')} dari ${data.total.toLocaleString('id-ID')} tamu yang sudah check-in`}
          </p>

          {visible.length === 0 ? (
            <p className="tamu-msg">Belum ada tamu yang check-in.</p>
          ) : (
            <div className="tamu-grid">
              {visible.map((g) => (
                <GuestCard
                  key={g.id}
                  guest={g}
                  canEdit={Boolean(data.can_edit)}
                  onEdited={() => load(query)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}

export default Tamu;
